import React, { useEffect, useMemo, useState } from 'react';
import { Card, Col, Row, Table as ReactTable } from 'react-bootstrap';
import PageInfo from '../PageInfo';
import { Pagination } from '../Pagination';
import '../Table.scss';
import LoadingOverlay from '../../feedback/LoadingOverlay';
import debounce from 'lodash.debounce';
import { AppState } from '../../store/store';
import { connect, ResolveThunks } from 'react-redux';
import { fetchContentSuccess, initialTableState, setPage, setQuery, setRefresh } from './tablesReducer';
import { SalvTextField, Size } from '@salvapp/ui-components';
import { clsx } from 'clsx';

interface Page<Item> {
  content: Item[];
  number: number;
  size: number;
  total: number;
}

interface TableProps<Item> {
  name: string;
  body: (items: Item[]) => React.ReactNode;
  header: (items: Item[]) => React.ReactNode;
  fetchContent: (page: number, size: number, query?: string) => Promise<Page<Item>>;
  title?: string;
  actions?: () => React.ReactNode;
  searchable?: boolean;
  responsive?: boolean;
  className?: string;
  hover?: boolean;
  pageSize?: number;
  disableBoxShadow?: boolean;
}

export const Table = <T,>({
  header,
  body,
  actions,
  title,
  tables,
  name,
  fetchContent,
  fetchContentSuccess,
  setPage,
  setQuery,
  setRefresh,
  searchable,
  responsive = true,
  className = '',
  hover = true,
  pageSize,
  disableBoxShadow = false,
}: ConnectProps & TableProps<T>): React.JSX.Element => {
  const { content, page, size, total, fetching, query, shouldRefresh } = tables[name] || initialTableState<T>(pageSize);
  const [queryString, setQueryString] = useState(query);
  const hasSearchOrActionsOrTitle = searchable || actions || title;

  const onSearchDebounced = useMemo(() => {
    return debounce((query: string | undefined) => {
      setQuery({ name, query });
    }, 300);
  }, [setQuery, name]);

  const fetchPage = (page: number) => {
    setPage({ name, page });
  };

  useEffect(() => {
    fetchContent(page, size, query).then((content) => fetchContentSuccess({ name, content, pageSize }));
  }, [fetchContentSuccess, fetchContent, name, page, query, size, pageSize]);

  useEffect(() => {
    if (shouldRefresh) {
      fetchContent(page, size, query).then((content) => {
        fetchContentSuccess({ name, content, pageSize });
        setRefresh({ name, shouldRefresh: false });
      });
    }
  }, [fetchContentSuccess, fetchContent, name, page, query, size, pageSize, setRefresh, shouldRefresh]);

  return (
    <React.Fragment>
      <Card body={false} className={clsx('table-iteration-123', disableBoxShadow && 'no-box-shadow')}>
        {hasSearchOrActionsOrTitle && (
          <Card.Header>
            {title && (
              <h4 className='card-header-title' aria-label={name}>
                {title}
              </h4>
            )}
            <Row className='align-items-center'>
              <Col>
                {searchable && (content.length > 0 || queryString) && (
                  <Row className='align-items-center'>
                    <SalvTextField
                      ariaLabel='Search'
                      size={Size.SMALL}
                      placeholder='Search...'
                      onChange={(value) => {
                        const targetValue = value?.length > 0 ? value : undefined;
                        setQueryString(targetValue);
                        onSearchDebounced(targetValue);
                      }}
                      value={queryString}
                    />
                  </Row>
                )}
              </Col>
              {actions && (
                <Col md='auto' className={'p-l-8'}>
                  {actions()}
                </Col>
              )}
            </Row>
          </Card.Header>
        )}
        {content.length > 0 ? (
          <LoadingOverlay loading={fetching}>
            <ReactTable striped responsive={responsive} hover={hover} size='sm' className={`card-table ${className}`}>
              <thead>{header(content)}</thead>
              <tbody>{body(content)}</tbody>
            </ReactTable>
            {Math.ceil(total / size) > 1 && (
              <Card.Footer>
                <div className='row align-items-center'>
                  <PageInfo total={total} page={page} size={size} items={content} />
                  <div className='col-auto'>
                    <Pagination page={page} size={size} total={total} fetchPage={fetchPage} />
                  </div>
                </div>
              </Card.Footer>
            )}
          </LoadingOverlay>
        ) : (
          <Card.Body className='p-y-16'>
            <div className='d-flex flex-row align-items-center'>
              <div className='flex-grow-1' />
              <span className='text-s nothing-to-show'>Nothing to show here, yet.</span>
              <div className='flex-grow-1' />
            </div>
          </Card.Body>
        )}
      </Card>
    </React.Fragment>
  );
};

const mapStateToProps = ({ tables }: AppState) => ({
  tables,
});

const mapDispatchToProps = {
  fetchContentSuccess,
  setPage,
  setQuery,
  setRefresh,
};

type ConnectProps = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const ConnectedTable = connect(mapStateToProps, mapDispatchToProps)(Table);

const ConnectedWithType = <T,>(props: TableProps<T>): React.JSX.Element => {
  // @ts-ignore
  return <ConnectedTable {...props} />;
};

export default ConnectedWithType;
